import * as React from 'react';
import { compose, lifecycle, setDisplayName, withState, withHandlers, withProps, branch, renderNothing } from 'recompose';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import { TranslateProps } from 'util/i18n';
import { skin, OemProps } from 'ui/styles/skin';
import { State } from 'reducers';
import { getLanguage } from 'selectors/shellSelectors';
import { FadeCollapse  } from 'ui/transitions';
import { ErrorMessage } from 'ui/shared/ErrorMessage';
import { Lock, User } from 'ui/shapes';
import { Button, Input } from 'ui/shared';

import './PasswordChangeForm.css';

const titleSrc = require('assets/images/oem/TitleLarge.png');

type Props = RecomposeProps & StateProps & DispProps & TranslateProps & OemProps & OwnProps;

export const RawPasswordChangeForm = ({
    oldPassword,
    newPassword,
    confirmPassword,
    errorKey,
    errorParams,
    handleSubmit,
    handleChangeConfirmPassword,
    handleChangeOldPassword,
    handleChangeNewPassword,
    t,
    styles,
}: Props) => {
    return <form
        id="passwordChange-form"
        onSubmit={ handleSubmit }
        style={ styles.container }
    >
        <img src={ titleSrc }/>
        <div style={ styles.body }>
            <div className="field">
                <label htmlFor="passwordChange-oldPassword">
                    { t('AE_ChangePassword_OldPassword') }
                </label>
                <div
                    style={ styles.field }
                >
                    <Lock width="10px" height="10px" style={ styles.icon }/>
                    <Input
                        id="passwordChange-oldPassword"
                        type="password"
                        value={ oldPassword }
                        maxLength={ 255 }
                        onChange={ handleChangeOldPassword }
                    />
                </div>
            </div>

            <div className="field">
                <label htmlFor="passwordChange-newPassword">
                    { t('AE_ChangePassword_NewPassword') }
                </label>
                <div
                    style={ styles.field }
                >
                    <Lock width="10px" height="10px" style={ styles.icon } />
                    <Input
                        id="passwordChange-newPassword"
                        type="password"
                        value={ newPassword }
                        maxLength={ 255 }
                        onChange={ handleChangeNewPassword }
                    />
                </div>
            </div>

            <div className="field">
                <label htmlFor="passwordChange-confirmPassword">
                    { t('AE_ChangePassword_ConfirmPassword') }
                </label>
                <div
                    style={ styles.field }
                >
                    <User width="10px" height="10px" style={ styles.icon } />
                    <Input
                        id="passwordChange-confirmPassword"
                        type="password"
                        value={ confirmPassword }
                        maxLength={ 255 }
                        onChange={ handleChangeConfirmPassword }
                    />
                </div>
            </div>

            <span className="button-bar">
                <Button
                    id="passwordChange-submit"
                    type="submit"
                    textKey="AE_ChangePassword_Title"
                />
            </span>

            <FadeCollapse visible={ !!errorKey } height="3em" duration={ 200 } unmountOnExit={ true }>
                <ErrorMessage
                    text={ t(errorKey, errorParams) }
                />
            </FadeCollapse>

            <div style={ styles.resetFloat }/>
        </div>
    </form>;
    }
;

export type OwnProps = {
    visible: boolean;
};
type RecomposeProps = {
    confirmPassword: string;
    oldPassword: string;
    newPassword: string;
    errorKey: string;
    errorParams: string[];
    handleChangeOldPassword: React.ChangeEventHandler<HTMLInputElement>;
    handleChangeNewPassword: React.ChangeEventHandler<HTMLInputElement>;
    handleChangeConfirmPassword: React.ChangeEventHandler<HTMLInputElement>;
    handleSubmit: React.FormEventHandler<HTMLFormElement>;
};
type _RecomposeProps = RecomposeProps & {
    _setOldPassword: (p: string) => void;
    _setNewPassword: (p: string) => void;
    _setConfirmPassword: (u: string) => void;
    _setErrorKey: (u: string) => void;
    _setErrorParams: (u: string) => void;
};
type StateProps = {
    langCode: string;
};
type DispProps = {
};

const mapStateToProps = (state: State): StateProps => ({
    langCode: getLanguage(state),
});

const mapDispatchToProps = ({
});

export const PasswordChangeForm = compose<Props, OwnProps>(
    setDisplayName('PasswordChangeForm'),
    skin,
    translate(),
    withState('oldPassword', '_setOldPassword', ''),
    withState('newPassword', '_setNewPassword', ''),
    withState('confirmPassword', '_setConfirmPassword', ''),
    withState('errorKey', '_setErrorKey', ''),
    withState('errorParams', '_setErrorParams', ''),
    connect(mapStateToProps, mapDispatchToProps),
    withHandlers<StateProps & DispProps & _RecomposeProps, Partial<RecomposeProps>>({
        handleChangeOldPassword: ({ _setOldPassword }) => (e: React.ChangeEvent<HTMLInputElement>) => {
            _setOldPassword(e.target.value);
        },
        handleChangeNewPassword: ({ _setNewPassword }) => (e: React.ChangeEvent<HTMLInputElement>) => {
            _setNewPassword(e.target.value);
        },
        handleChangeConfirmPassword: ({ _setConfirmPassword }) => (e: React.ChangeEvent<HTMLInputElement>) => {
            _setConfirmPassword(e.target.value);
        },
        handleSubmit: ({ oldPassword, newPassword, confirmPassword, _setErrorKey, _setErrorParams }) => (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();

            fetch('/Client/api/users/me/password', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams({
                    oldPassword, newPassword, confirmPassword,
                }),
            }).then((result: any) => {
                return result.json();
            }, _error => {
                _setErrorKey('AE_ChangePassword_Error_ServerError');
            }).then((result: any) => {
                result = result.result;
                if (result.isPasswordChanged) {
                    window.location.reload();
                    return;
                }

                _setErrorKey(result.validationKey);
                _setErrorParams(result.validationParams);
            }, _error => {
                _setErrorKey('AE_ChangePassword_Error_ServerError');
            });

            return false;
        },
    }),
    withProps(({ styles }: OemProps) => ({
        styles: {
            ...styles,
            container: {
                ...styles.Workspace,
                ...styles.Bordered,
                maxWidth: '352px', // because the title image is 350px; +2 x 1px for left & right border
            },
            body: {
                padding: '2em 2em 1.5em 2em',
            },
            field: {
                ...styles.Input,
                padding: '6px 10px',
            },
            icon: {
                fill: styles.Input.color,
            },
            resetFloat: {
                clear: 'both',
                float: 'none',
            },
        },
    })),
    lifecycle({
        componentDidUpdate(oldProps: _RecomposeProps & OwnProps) {
            // Reset the confirmPassword and password whenever the PasswordChangeForm visibility changes
            if (oldProps.visible !== this.props.visible) {
                this.props._setConfirmPassword('');
                this.props._setOldPassword('');
                this.props._setNewPassword('');
            }
        },
    }),
    branch<Props>(({ visible }) => !visible, renderNothing),
)(RawPasswordChangeForm);



// WEBPACK FOOTER //
// ./src/ui/password_change/PasswordChangeForm.tsx